import { floatVal, Nullable } from '@jamesgmarks/utilities';
import { roundFloatToCents } from '@hydra/internal';
import { EHydraCreditNoteState } from '../entity-interfaces/EHydraCreditNoteState';

/**
 * Minimal shape required to total a credit note. Satisfied by the `CreditNote` entity.
 *
 * `string | number` is deliberate HERE and only here, because this shape has two real producers:
 * a SAVED note read from the API, whose `decimal` columns arrive as strings, and an UNSAVED note
 * in `CreditNoteSingleView`, which doubles as the editor and totals numbers it derived from the
 * credit lines in browser state. Narrowing to `string` would force the editor to stringify
 * numbers purely to satisfy a type. Every other shape in this file is API-only and takes
 * `string` alone.
 *
 * `Nullable` because the nulls are real: sampled live, 19 of 500 credit notes carry a null
 * `subtotalAmount` or `taxAmount`. That is what the `?? 0` guards downstream are for.
 */
export interface ICreditNoteTotalSource {
  subtotalAmount: Nullable<string | number>;
  taxAmount: Nullable<string | number>;
}

/**
 * Minimal shape of an applied-credit row. Satisfied by the `CreditApplied` entity.
 *
 * `taxAmount` is deliberately `Nullable` even though `CreditApplied` declares its column
 * `nullable: false`. The entity is wrong: sampling the live table, 43 of 500 rows hold a null
 * `tax_amount`. Trusting the entity here would put a null through `floatVal` unguarded.
 */
export interface IAppliedCreditSource {
  amount: Nullable<string>;
  taxAmount: Nullable<string>;
  hydraState: Nullable<string>;
}

/**
 * Minimal shape required to derive a credit note's available credit from its applied rows.
 *
 * `creditsApplied` stays OPTIONAL, unlike every other field in this file. Its absence is
 * meaningful rather than sloppy: `getCreditNoteAvailableCredit` takes an `appliedTotal` override,
 * and the editor in `CreditNoteSingleView` uses it to pass a total it already computed instead of
 * the rows. Requiring the key would force that caller to pass a `null` it does not have.
 */
export interface ICreditNoteAvailableCreditSource extends ICreditNoteTotalSource {
  creditsApplied?: Nullable<IAppliedCreditSource[]>;
}

/**
 * Front-end counterpart of `getCreditNoteAvailableCredit` in `src/hydra-api/creditNotes.ts`.
 *
 * The back end computes these figures with `decimal.js` via `src/hydra-api/currency-math`.
 * `decimal.js` is a root dependency and is NOT declared in `src/hydra-ui/package.json`, so this
 * module reaches the same answers by rounding to cents at every step with `roundFloatToCents`
 * instead. `tests/hydra-ui/src/app-utils/credit-notes.test.ts` pins the two implementations to the
 * same results.
 */

/**
 * Returns the full value of a credit note: `subtotalAmount + taxAmount`, rounded to cents.
 *
 * NOTE: `roundingAdjustment` is deliberately NOT included, matching `makeCreditNoteSummary` and
 * `getCreditNoteTotal` on the back end.
 *
 * @param creditNote Credit note carrying `subtotalAmount` / `taxAmount`.
 * @returns The tax-inclusive credit note total.
 */
export const getCreditNoteTotal = (creditNote: ICreditNoteTotalSource): number => (
  roundFloatToCents(floatVal(creditNote.subtotalAmount ?? 0) + floatVal(creditNote.taxAmount ?? 0))
);

/**
 * Sums the `active` applied-credit rows of a credit note, rounded to cents.
 *
 * Both `amount` AND `taxAmount` are summed. `credits_applied.amount` is written tax-EXCLUSIVE by the
 * manual-application path (`src/routers/credits.ts`), while the bulk Xero allocation path writes the
 * tax-inclusive figure into `amount` and leaves `taxAmount` at its `0` default. Adding both is
 * therefore correct for rows from either path; summing `amount` alone under-counts rows from the
 * first.
 *
 * @param creditsApplied The credit note's applied-credit rows (non-active rows are ignored).
 * @returns The total credit consumed.
 */
export const sumActiveCreditsApplied = (
  creditsApplied?: Nullable<IAppliedCreditSource[]>,
): number => (
  roundFloatToCents(
    (creditsApplied ?? [])
      .filter((applied) => applied.hydraState === 'active')
      .reduce(
        (runningTotal, applied) => (
          runningTotal + floatVal(applied.amount ?? 0) + floatVal(applied.taxAmount ?? 0)
        ),
        0,
      ),
  )
);

/**
 * Returns the credit still available on a credit note.
 *
 * `available = (subtotalAmount + taxAmount) - sum(active applied)`, rounded to cents at each step so
 * an exactly-consumed credit note returns `0` rather than a sub-cent floating-point residual such as
 * `-1.7763568394002505e-15` (which would render as `-$0.00`).
 *
 * @param creditNote Credit note carrying the amounts and its `creditsApplied` rows.
 * @param appliedTotal Overrides the applied total. Defaults to the note's own active applied rows.
 * @returns The remaining available credit. May be negative if the note has been over-applied.
 */
export const getCreditNoteAvailableCredit = (
  creditNote: ICreditNoteAvailableCreditSource,
  appliedTotal: number = sumActiveCreditsApplied(creditNote.creditsApplied),
): number => (
  roundFloatToCents(getCreditNoteTotal(creditNote) - appliedTotal)
);

/**
 * Determines whether a credit note has been fully applied (i.e. no remaining available credit).
 *
 * A credit note is fully applied when it is in the `sent` state AND its cent-rounded available
 * credit is zero or below. The rounding matters: with raw float arithmetic the residual's sign is
 * arbitrary, so a fully-consumed note could land on `+1.11e-16` and be reported as NOT fully
 * applied, silently dropping it from the "Applied" filter.
 *
 * @param creditNote The credit note to evaluate, including its `state` and `creditsApplied` rows.
 * @returns `true` if the credit note is sent and has no remaining available credit.
 */
export const isCreditNoteFullyApplied = (
  creditNote: ICreditNoteAvailableCreditSource & { state?: Nullable<EHydraCreditNoteState> },
): boolean => (
  creditNote.state === EHydraCreditNoteState.sent && getCreditNoteAvailableCredit(creditNote) <= 0
);

/** Minimal shape of an invoice as referenced from the credit-note list view. */
export interface IListViewInvoiceRef {
  id: Nullable<number>;
  invoiceNumber: Nullable<string>;
}

/** Minimal shape of a credit row, which points at the invoice the credit was drawn FROM. */
export interface ICreditWithInvoice {
  invoice: Nullable<IListViewInvoiceRef>;
  invoiceId: Nullable<number>;
  amount: Nullable<string>;
  taxAmount: Nullable<string>;
  hydraState: Nullable<string>;
}

/** Minimal shape of an applied-credit row, which points at the invoice the credit was applied TO. */
export interface IAppliedCreditWithInvoice extends IAppliedCreditSource {
  invoice: Nullable<IListViewInvoiceRef>;
  invoiceId: Nullable<number>;
}

/** One invoice related to a credit note, with the total amount linking the two. */
export interface ICreditNoteInvoiceLink {
  invoiceId: number;
  invoiceNumber: Nullable<string>;
  amount: number;
}

/**
 * Collapses per-row invoice references into one entry per invoice, summing their amounts.
 *
 * A credit note can hold several credits drawn from the SAME invoice (and can be applied to one
 * invoice in several instalments), so the raw rows would otherwise render as duplicate chips.
 *
 * @param links Raw per-row links, possibly containing repeated `invoiceId`s.
 * @returns One link per invoice, amounts summed, ordered by invoice id.
 */
const groupLinksByInvoice = (links: ICreditNoteInvoiceLink[]): ICreditNoteInvoiceLink[] => (
  Object
    .values(
      links.reduce<Record<number, ICreditNoteInvoiceLink>>(
        (grouped, link) => ({
          ...grouped,
          [link.invoiceId]: {
            invoiceId: link.invoiceId,
            invoiceNumber: link.invoiceNumber ?? grouped[link.invoiceId]?.invoiceNumber ?? null,
            amount: roundFloatToCents((grouped[link.invoiceId]?.amount ?? 0) + link.amount),
          },
        }),
        {},
      ),
    )
    .sort((left, right) => left.invoiceId - right.invoiceId)
);

/**
 * Builds the invoice link for a row that carries either a loaded `invoice` relation or a bare
 * `invoiceId`, returning `null` when the row points at no invoice at all.
 *
 * The `invoice` relation is preferred because it carries the human-readable `invoiceNumber`; the
 * bare `invoiceId` is the fallback for when the relation was not requested by the caller.
 */
const toInvoiceLink = (
  row: { invoice?: Nullable<IListViewInvoiceRef>; invoiceId?: Nullable<number> },
  amount: number,
): Nullable<ICreditNoteInvoiceLink> => {
  const invoiceId = row.invoice?.id ?? row.invoiceId ?? null;

  if (invoiceId === null) { return null; }

  return {
    invoiceId,
    invoiceNumber: row.invoice?.invoiceNumber ?? null,
    amount,
  };
};

/**
 * Returns the invoices a credit note draws its credit FROM (its "source" invoices).
 *
 * Sourced from the note's `credits` rows, each of which records the invoice the credit was raised
 * against. Non-`active` credits (deleted/void) are excluded because they no longer draw anything
 * from their invoice. Requires the `cn.credits` and `cr.invoice` relations to be loaded.
 *
 * @param creditNote Credit note carrying its `credits` rows.
 * @returns One entry per distinct source invoice, with the total credit drawn from it.
 */
export const getCreditNoteSourceInvoices = (
  creditNote: { credits?: Nullable<ICreditWithInvoice[]> },
): ICreditNoteInvoiceLink[] => (
  groupLinksByInvoice(
    (creditNote.credits ?? [])
      .filter((credit) => credit.hydraState === 'active')
      .map((credit) => toInvoiceLink(
        credit,
        roundFloatToCents(floatVal(credit.amount ?? 0) + floatVal(credit.taxAmount ?? 0)),
      ))
      .filter((link): link is ICreditNoteInvoiceLink => link !== null),
  )
);

/**
 * Returns the invoices a credit note has been APPLIED to.
 *
 * Sourced from the note's `creditsApplied` rows. Only `active` rows count, matching
 * {@link sumActiveCreditsApplied}, so the chip totals reconcile with the note's available credit.
 * Both `amount` and `taxAmount` are summed for the reason documented on that function. Requires the
 * `cn.creditsApplied` and `ca.invoice` relations to be loaded.
 *
 * @param creditNote Credit note carrying its `creditsApplied` rows.
 * @returns One entry per distinct applied-to invoice, with the total credit applied to it.
 */
export const getCreditNoteAppliedInvoices = (
  creditNote: { creditsApplied?: Nullable<IAppliedCreditWithInvoice[]> },
): ICreditNoteInvoiceLink[] => (
  groupLinksByInvoice(
    (creditNote.creditsApplied ?? [])
      .filter((applied) => applied.hydraState === 'active')
      .map((applied) => toInvoiceLink(
        applied,
        roundFloatToCents(floatVal(applied.amount ?? 0) + floatVal(applied.taxAmount ?? 0)),
      ))
      .filter((link): link is ICreditNoteInvoiceLink => link !== null),
  )
);
